import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Zap, Loader2, ChevronDown, ChevronUp, Clock, Rss, AlertTriangle,
  Info, BookOpen, Wallet, GraduationCap, Home, ShieldAlert, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const EVENT_ICONS = {
  "blog.article.published": BookOpen,
  "caf.aid.updated": Wallet,
  "crous.period.opened": GraduationCap,
  "scam.alert.new": ShieldAlert,
  "housing.guide.new": Home,
  "app.update.major": Sparkles,
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}

export default function AutoBroadcastSection({ onCampaignSent }) {
  const [expanded, setExpanded] = useState(true);
  const [types, setTypes] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [eventType, setEventType] = useState("blog.article.published");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [coverUrl, setCoverUrl] = useState("");
  const [articleId, setArticleId] = useState("");
  const [autoNotify, setAutoNotify] = useState(true);
  const [force, setForce] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p] = await Promise.all([
        api.get("/admin/events/types"),
        api.get("/admin/events/pending?status=pending"),
      ]);
      setTypes(t.data || []);
      setPending(p.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const emit = async () => {
    if (!title || title.length < 2) { toast.error("Titre obligatoire (2 chars min)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/events/emit", {
        event_type: eventType,
        title, description, link: link || null,
        cover_image_url: coverUrl || null,
        article_id: articleId || null,
        auto_notify: autoNotify,
        force,
      });
      if (data.buffered && !data.sent) {
        toast.info(`📦 Événement mis en file d'attente (cooldown actif ou auto-notify off)`);
      } else if (data.sent) {
        toast.success(`✅ ${data.delivered} notifications envoyées${data.dispatched > 1 ? ` (${data.dispatched} articles regroupés)` : ""}`);
        if (typeof onCampaignSent === "function") onCampaignSent();
      } else {
        toast.info("Événement enregistré.");
      }
      setTitle(""); setDescription(""); setLink(""); setCoverUrl(""); setArticleId("");
      setForce(false);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const dispatchType = async (evType) => {
    if (!window.confirm(`Envoyer maintenant tous les événements ${evType} en attente ?\n(bypass du cooldown)`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${evType}/dispatch?force=true`);
      if (data.sent) {
        toast.success(`✅ ${data.delivered} destinataires · ${data.dispatched} événements regroupés`);
        if (typeof onCampaignSent === "function") onCampaignSent();
      } else {
        toast.info("Rien à envoyer.");
      }
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    } finally {
      setBusy(false);
    }
  };

  const currentType = types.find((t) => t.key === eventType);

  return (
    <section
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      data-testid="autobroadcast-section"
    >
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full px-6 sm:px-8 py-5 flex items-center justify-between hover:bg-slate-50 transition-colors"
        data-testid="autobroadcast-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-500 text-white flex items-center justify-center">
            <Rss size={18} />
          </div>
          <div className="text-left">
            <h2 className="font-display font-bold text-xl text-slate-900">
              Broadcast automatique (par événement)
            </h2>
            <p className="text-sm text-slate-500">
              Nouvel article, aide CAF, période CROUS, alerte arnaque… un seul endroit, anti-spam intégré.
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 p-6 sm:p-8 space-y-6">
          {/* Pending queue overview */}
          {pending.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="autobroadcast-pending">
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-amber-900 text-sm">
                    {pending.length} événement{pending.length > 1 ? "s" : ""} en attente
                  </p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    Ces éléments seront regroupés dans la prochaine notification lorsque le cooldown expirera. Tu peux forcer l'envoi maintenant.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {Object.entries(pending.reduce((acc, p) => {
                      acc[p.event_type] = (acc[p.event_type] || 0) + 1; return acc;
                    }, {})).map(([et, n]) => (
                      <li key={et} className="flex items-center justify-between gap-3 text-xs">
                        <span className="text-amber-900 font-mono">{et}</span>
                        <span className="text-amber-700">{n} en file</span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300 text-amber-800 hover:bg-amber-100 h-7 text-xs"
                          onClick={() => dispatchType(et)}
                          disabled={busy}
                          data-testid={`autobroadcast-dispatch-${et}`}
                        >
                          <Zap size={11} className="mr-1" /> Envoyer maintenant
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* New event form */}
          <div>
            <p className="text-sm font-semibold text-slate-900 mb-3">Déclencher un événement</p>

            {/* Event type tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {types.map((t) => {
                const Icon = EVENT_ICONS[t.key] || Info;
                const active = eventType === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setEventType(t.key)}
                    className={`px-3 py-2.5 rounded-xl border text-left transition-colors ${
                      active
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                        : "border-slate-200 hover:border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid={`autobroadcast-type-${t.key}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={active ? "text-indigo-600" : "text-slate-500"} />
                      <span className="text-xs font-medium">{t.label}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      cooldown {t.cooldown_hours}h · {t.coalesce ? "coalesce" : "immédiat"}
                    </p>
                  </button>
                );
              })}
            </div>

            {/* Info banner about current type */}
            {currentType && (
              <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-start gap-2">
                <Info size={13} className="text-indigo-500 shrink-0 mt-0.5" />
                <div>
                  <strong>{currentType.label}</strong> — Catégorie : <span className="font-mono">{currentType.category}</span> ·
                  Priorité : <span className="font-mono">{currentType.priority}</span> ·
                  {currentType.coalesce
                    ? " Plusieurs événements le même jour = 1 notif digest (anti-spam)."
                    : " Envoi immédiat, non regroupé."}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="ab-title">Titre <span className="text-rose-500">*</span></Label>
                <Input
                  id="ab-title" value={title} onChange={(e) => setTitle(e.target.value)}
                  maxLength={200} placeholder="Ex : CAF 2026 — Barèmes mis à jour"
                  data-testid="ab-title-input" className="mt-1.5"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ab-desc">Courte description</Label>
                <Textarea
                  id="ab-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
                  maxLength={400}
                  placeholder="Résume en 1-2 phrases. Ce sera le corps de la notif."
                  data-testid="ab-description-input" className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="ab-link">Lien vers l'article/page</Label>
                <Input
                  id="ab-link" value={link} onChange={(e) => setLink(e.target.value)}
                  placeholder="/blog/mon-article"
                  data-testid="ab-link-input" className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="ab-cover">URL image de couverture</Label>
                <Input
                  id="ab-cover" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)}
                  placeholder="https://…/cover.jpg"
                  data-testid="ab-cover-input" className="mt-1.5"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="ab-article-id">ID métier (optionnel — évite les doublons)</Label>
                <Input
                  id="ab-article-id" value={articleId} onChange={(e) => setArticleId(e.target.value)}
                  placeholder="Ex : caf-etudiant-2026"
                  data-testid="ab-article-id-input" className="mt-1.5"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer" data-testid="ab-auto-notify-row">
                <Switch checked={autoNotify} onCheckedChange={setAutoNotify} data-testid="ab-auto-notify-switch" />
                <div className="text-sm">
                  <p className="font-medium text-slate-900">Diffuser automatiquement</p>
                  <p className="text-xs text-slate-500">
                    Décoche pour <strong>uniquement enregistrer</strong> sans notifier. Tu pourras envoyer plus tard.
                  </p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer" data-testid="ab-force-row">
                <Switch checked={force} onCheckedChange={setForce} data-testid="ab-force-switch" />
                <div className="text-sm">
                  <p className="font-medium text-slate-900 flex items-center gap-1">
                    Ignorer le cooldown <AlertTriangle size={12} className="text-amber-500" />
                  </p>
                  <p className="text-xs text-slate-500">
                    Envoie même si une notif de la même catégorie a été envoyée récemment. À utiliser avec parcimonie.
                  </p>
                </div>
              </label>
            </div>

            <Button
              onClick={emit}
              disabled={busy || !title}
              className="mt-5 gap-1.5 bg-fuchsia-600 hover:bg-fuchsia-700"
              data-testid="ab-emit-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              Déclencher l'événement
            </Button>
          </div>

          {/* Pending items detail */}
          {pending.length > 0 && (
            <details className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="autobroadcast-pending-detail">
              <summary className="text-xs font-medium text-slate-700 cursor-pointer">
                Détail des {pending.length} événements en attente
              </summary>
              <ul className="mt-3 divide-y divide-slate-200">
                {pending.map((p) => (
                  <li key={p.id} className="py-2 text-xs">
                    <div className="flex items-baseline gap-2">
                      <span className="font-mono text-slate-500">{p.event_type}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">{formatDate(p.created_at)}</span>
                    </div>
                    <p className="font-medium text-slate-800 mt-0.5">{p.payload?.title}</p>
                    {p.payload?.description && (
                      <p className="text-slate-500 line-clamp-1">{p.payload.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}

          {loading && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Chargement…
            </p>
          )}
        </div>
      )}
    </section>
  );
}
